import {
  ReadonlyWatchable,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { CellComponentProps } from '@cotera/client/v0/components/data-grid/types';
import { Relation, TC, Ty } from '@cotera/era';
import { cn } from '@cotera/client/v0/lib/utils';
import { timeSeries } from '@cotera/client/app/components/utils/format/time-series';
import { UTCDate } from '@cotera/client/app/components/utils';
import { Link } from 'react-router-dom';
import { Sort } from './types';

const MAX_TEXT_LENGTH = 100;

// Default components
export const DefaultCell: React.FC<
  Omit<CellComponentProps, 'value'> & {
    className?: string;
    model: {
      sort: ReadonlyWatchable<Sort[]>;
    };
    children: React.ReactNode;
    onMouseOver?: () => void;
  }
> = ({ className, model, column, children, onMouseOver }) => {
  const sort = useWatchableValue(model.sort);
  const isActive = sort.some((x) => x.column === column.key);

  return (
    <div
      onMouseEnter={onMouseOver}
      className={cn(
        'h-full px-3 py-2 truncate text-sm flex items-center',
        isActive ? 'bg-yellow-200/20' : '',
        children === null ? 'text-muted-foreground' : '',
        className
      )}
    >
      {children === null ? 'NULL' : children}
    </div>
  );
};

export const BaseCellRenderer: React.FC<
  CellComponentProps & {
    rel: Relation;
    model: {
      sort: ReadonlyWatchable<Sort[]>;
    };
  }
> = ({ column, rel, model, ...props }) => {
  const ty = rel.attributes[column.key] ?? column.ty ?? Ty.ty('string');

  if (props.value !== null && isLink(ty)) {
    const valueStruct = props.value as {
      __link: string;
      text: string;
      action: 'tab' | 'navigate';
    };
    const { __link, text, action } = valueStruct;

    return (
      <DefaultCell
        model={model}
        column={column}
        {...props}
        children={
          <Link
            className="justify-between flex hover:text-primary-text"
            to={__link}
            target={action === 'tab' ? '_blank' : undefined}
          >
            {text}
          </Link>
        }
      />
    );
  }

  if (TC.isNumeric(ty)) {
    return (
      <DefaultCell
        children={props.value}
        model={model}
        column={column}
        className="text-right justify-end w-full"
        {...props}
      />
    );
  }

  if (TC.isTimestamp(ty)) {
    return (
      <DefaultCell
        model={model}
        column={column}
        {...props}
        children={
          props.value !== null
            ? timeSeries(new UTCDate(props.value), 'day')
            : null
        }
      />
    );
  }

  if (TC.isStruct(ty) || TC.isArray(ty)) {
    return (
      <DefaultCell
        model={model}
        column={column}
        {...props}
        children={props.value !== null ? JSON.stringify(props.value) : null}
      />
    );
  }

  // Runtime check for object/array values to handle cases where type isn't properly detected
  // This prevents "[object Object]" from being displayed
  if (
    props.value !== null &&
    typeof props.value === 'object' &&
    !(props.value instanceof Date)
  ) {
    return (
      <DefaultCell
        model={model}
        column={column}
        {...props}
        children={JSON.stringify(props.value)}
      />
    );
  }

  return (
    <DefaultCell
      model={model}
      column={column}
      {...props}
      children={props.value !== null ? truncateText(String(props.value)) : null}
    />
  );
};

const isLink = (attr: Ty.ExtendedAttributeType) => {
  const ty = Ty.shorthandToTy(attr);
  if (TC.isStruct(ty)) {
    return ty.ty.k === 'struct' && '__link' in ty.ty.fields;
  }

  return false;
};

const truncateText = (text: string): string => {
  if (text.length <= MAX_TEXT_LENGTH) {
    return text;
  }
  return text.slice(0, MAX_TEXT_LENGTH) + '...';
};
